import React, { useState } from "react";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Logo } from "@/components/Logo";
import { subscribeAcademy } from "@/lib/api";
import { toast } from "sonner";

const BENEFITS = [
  "Full Academy access",
  "Exclusive monthly lessons",
  "Ongoing research updates",
  "Member-only community",
  "Live monthly Q&A or masterclass",
  "Downloadable worksheets and tools",
  "Early access to new chapters, assessments & frameworks",
  "Discounts on books, certifications & events",
];

const PLANS = [
  { key: "founding", name: "Founding Member", price: "29.99", tag: "Locked in for life",
    blurb: "For our earliest community. Your rate stays $29.99/mo for life as long as you remain subscribed.", featured: true },
  { key: "standard", name: "Standard", price: "49.99", tag: "Full membership",
    blurb: "Full Academy access with everything included, billed monthly.", featured: false },
];

const Academy = () => {
  const [loading, setLoading] = useState("");

  const subscribe = async (plan) => {
    setLoading(plan);
    try {
      const { checkout_url } = await subscribeAcademy(plan, window.location.origin);
      window.location.href = checkout_url;
    } catch {
      toast.error("Unable to start subscription. Please try again.");
      setLoading("");
    }
  };

  return (
    <main className="bg-cream min-h-screen pt-32" data-testid="academy-page">
      <section className="bg-emerald text-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow text-gold mb-6 flex items-center gap-3"><Sparkles className="w-4 h-4" /> The Love Genome™ Academy</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-3xl">
              Keep growing, <span className="italic text-gold">every month.</span>
            </h1>
            <p className="font-editorial italic text-2xl text-cream/80 mt-8 max-w-2xl">
              Lessons, live Q&A, tools, and community — a living practice of the framework, delivered monthly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-8">
          {PLANS.map((p) => (
            <Reveal key={p.key}>
              <div className={`h-full p-10 md:p-12 border flex flex-col ${p.featured ? "bg-emerald text-cream border-emerald" : "bg-surface text-ink border-line"}`}
                data-testid={`plan-${p.key}`}>
                <div className="flex items-center justify-between mb-6">
                  <p className={`eyebrow ${p.featured ? "text-gold" : "text-emerald"}`}>{p.tag}</p>
                  {p.featured && <Logo className="w-9 h-9" />}
                </div>
                <h2 className="font-display text-3xl md:text-4xl mb-3">{p.name}</h2>
                <div className="flex items-end gap-1 mb-4">
                  <span className="font-display text-5xl">${p.price}</span>
                  <span className={`font-body text-sm mb-2 ${p.featured ? "text-cream/60" : "text-ink/50"}`}>/ month</span>
                </div>
                <p className={`font-body mb-8 ${p.featured ? "text-cream/80" : "text-ink/70"}`}>{p.blurb}</p>
                <ul className="space-y-3 mb-10 flex-1">
                  {BENEFITS.map((b) => (
                    <li key={b} className="flex items-start gap-3 font-body text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-gold" : "text-emerald"}`} />
                      <span className={p.featured ? "text-cream/90" : "text-ink/80"}>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => subscribe(p.key)}
                  disabled={loading === p.key}
                  className={`${p.featured ? "btn-gold" : "btn-emerald"} w-full justify-center disabled:opacity-60`}
                  data-testid={`subscribe-${p.key}`}
                >
                  {loading === p.key ? "Redirecting…" : <>Become a {p.name} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="text-center font-body text-xs text-ink/45 mt-10 px-6">
          Cancel anytime. Founding rate is honored for life while your subscription remains active. Secure billing via Stripe.
        </p>

        <div className="max-w-[1200px] mx-auto px-6 md:px-10 mt-16">
          <div className="border border-gold/40 bg-gold/5 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
            <div className="flex-1">
              <p className="eyebrow text-emerald mb-3">Give the gift of flourishing</p>
              <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-3">
                Gift-A-Membership.
              </h3>
              <p className="font-body text-ink/70 text-sm md:text-base max-w-lg">
                Send a friend, mentor, or loved one a month, a season, or a full year of Academy access —
                delivered by email or as a printable gift certificate.
              </p>
            </div>
            <a
              href="/academy/gift"
              className="btn-emerald shrink-0"
              data-testid="gift-academy-cta"
            >
              Gift a membership <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Academy;
